import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import BrandSelect from "./BrandSelect";
import { Brand } from "../../types/paymentProcessor";
import { useApi } from "../../hooks/useApi";
import RepresentmentStartDateField from "./RepresentmentStartDateField";
import { useRepresentmentStartDate } from "../../hooks/useRepresentmentStartDate";

interface PayPalDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
  showRepresentmentStartDate?: boolean;
  brands: Brand[];
  refreshBrands: () => void;
  createBrandUrl?: string;
}

export default function PayPalDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
  showRepresentmentStartDate,
  brands,
  refreshBrands,
  createBrandUrl,
}: PayPalDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState({ name: '', client_id: '', client_secret: '' });
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const representmentStartDate = useRepresentmentStartDate();
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setFormData({ name: '', client_id: '', client_secret: '' });
    setSelectedBrandId('');
    representmentStartDate.reset();
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    try {
      await post(apiUrl, {
        name: formData.name,
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        brandId: selectedBrandId,
        ...(showRepresentmentStartDate ? representmentStartDate.payload : {}),
      });

      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      setFormError(err.message || 'Failed to link PayPal account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link PayPal Account</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, background: '#f5f5f7', p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
            PayPal App Setup Instructions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To link a PayPal account, create a new app in the PayPal Developer Portal:
          </Typography>
          <ul style={{ margin: '8px 0 0 20px', color: 'rgba(0, 0, 0, 0.8)', padding: 0 }}>
            <li>
              <Typography variant="body2" component="span">
                Navigate to <strong>https://developer.paypal.com/</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Ensure you are in <strong>Live mode</strong> (not Sandbox)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Go to <strong>Apps & Credentials</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Click <strong>Create App</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Enter <strong>App Name: Chargemont</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                If prompted, select <strong>Type: Merchant</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Click <strong>Create App</strong>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                Verify that <strong>"Customer disputes"</strong> and <strong>"Transaction search"</strong> checkboxes are selected
              </Typography>
            </li>
          </ul>
        </Box>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} component="form" autoComplete="off">
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
            autoComplete="off"
          />
          <TextField
            label="Client ID"
            name="client_id"
            value={formData.client_id}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
            autoComplete="off"
          />
          <TextField
            label="Secret Key"
            name="client_secret"
            type="password"
            value={formData.client_secret}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
            autoComplete="new-password"
          />
          <BrandSelect
            brands={brands}
            value={selectedBrandId}
            onChange={(brandId) => setSelectedBrandId(brandId)}
            refreshBrands={refreshBrands}
            disabled={submitting}
            createBrandUrl={createBrandUrl}
          />
          {showRepresentmentStartDate && (
            <RepresentmentStartDateField
              value={representmentStartDate.value}
              onChange={representmentStartDate.setValue}
              disabled={submitting}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !formData.name || !formData.client_id || !formData.client_secret || !selectedBrandId || (showRepresentmentStartDate && representmentStartDate.invalid)}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
